import { Button, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import { useAccess } from '../../../auth/useAccess.js';
import { contrastWithWhite, EMPTY_COLOUR_INPUT, normalizeHex } from '../../../lib/contrast.js';
import { useCompanySettingsMutation, useSettingsQuery } from '../useSettingsQuery.js';

/**
 * The company's own colours for the crew app's "Company" theme: `brandPrimary` (buttons and the
 * clock card, white text on top), `brandAccent` (marks and highlights on a light screen), and
 * `crewThemeLock` (crew cannot switch to Cool or Warm). The contrast is checked as the owner types,
 * against the same lines the server enforces: 4.5:1 for white text on the main colour (WCAG AA
 * text), 3:1 for the accent on white (WCAG non-text). A colour that fails cannot be saved.
 *
 * Only an owner changes these; anyone else who can open Settings reads them. The card hides until
 * the server sends all three keys, because the server refuses a key it does not know.
 */

const MIN_PRIMARY = 4.5;
const MIN_ACCENT = 3;
const INPUT =
  'h-11 w-full min-w-0 rounded-md border border-border bg-background px-3 font-mono text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60';

type Check = { state: 'empty' } | { state: 'invalid' } | { state: 'ok' | 'low'; ratio: number };

/** An empty field means "no company colour"; anything else must be a colour that clears the line. */
export function checkColour(value: string, min: number): Check {
  if (value.trim() === '') return { state: 'empty' };
  const ratio = contrastWithWhite(value);
  if (ratio === null) return { state: 'invalid' };
  return { state: ratio >= min ? 'ok' : 'low', ratio };
}

function ColourField({
  id,
  label,
  hint,
  value,
  onChange,
  check,
  lowMessage,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (next: string) => void;
  check: Check;
  lowMessage: string;
}) {
  const { t } = useTranslation();
  const ratio = 'ratio' in check ? `${check.ratio.toFixed(1)}:1` : '';
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="font-medium text-foreground text-sm">
        {label}
      </label>
      <p id={`${id}-hint`} className="mt-1 text-muted-foreground text-xs">
        {hint}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          aria-label={t('settings:brand.pick', { label })}
          className="h-11 w-12 shrink-0 cursor-pointer rounded-md border border-border bg-background p-1 disabled:cursor-not-allowed"
          value={normalizeHex(value) ?? EMPTY_COLOUR_INPUT}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          id={id}
          className={INPUT}
          value={value}
          placeholder={t('settings:brand.placeholder')}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={`${id}-hint ${id}-check`}
          aria-invalid={check.state === 'invalid' || check.state === 'low'}
        />
      </div>
      <p
        id={`${id}-check`}
        aria-live="polite"
        className={
          check.state === 'invalid' || check.state === 'low'
            ? 'mt-1 text-destructive text-xs'
            : 'mt-1 text-muted-foreground text-xs'
        }
      >
        {check.state === 'empty' ? t('settings:brand.notSet') : null}
        {check.state === 'invalid' ? t('settings:brand.invalid') : null}
        {check.state === 'ok' ? t('settings:brand.passes', { ratio }) : null}
        {check.state === 'low' ? `${ratio}. ${lowMessage}` : null}
      </p>
    </div>
  );
}

export function BrandColoursCard() {
  const { t } = useTranslation();
  const canEdit = useAccess()({ minRole: 'Admin' });
  const companySettings = useSettingsQuery().data?.companySettings;
  const save = useCompanySettingsMutation();

  const known =
    !!companySettings &&
    'brandPrimary' in companySettings &&
    'brandAccent' in companySettings &&
    typeof companySettings.crewThemeLock === 'boolean';
  const stored = {
    primary: typeof companySettings?.brandPrimary === 'string' ? companySettings.brandPrimary : '',
    accent: typeof companySettings?.brandAccent === 'string' ? companySettings.brandAccent : '',
    lock: companySettings?.crewThemeLock === true,
  };

  const [primary, setPrimary] = useState(stored.primary);
  const [accent, setAccent] = useState(stored.accent);
  const [lock, setLock] = useState(stored.lock);

  useEffect(() => {
    setPrimary(stored.primary);
    setAccent(stored.accent);
    setLock(stored.lock);
  }, [stored.primary, stored.accent, stored.lock]);

  if (!known) return null;

  const primaryCheck = checkColour(primary, MIN_PRIMARY);
  const accentCheck = checkColour(accent, MIN_ACCENT);
  const bothSet = primaryCheck.state === 'ok' && accentCheck.state === 'ok';
  const savable =
    (primaryCheck.state === 'ok' || primaryCheck.state === 'empty') &&
    (accentCheck.state === 'ok' || accentCheck.state === 'empty');
  // The lock only means something once there is a company theme to lock the crew to.
  const lockValue = lock && bothSet;
  const next = {
    brandPrimary: primaryCheck.state === 'ok' ? normalizeHex(primary) : null,
    brandAccent: accentCheck.state === 'ok' ? normalizeHex(accent) : null,
    crewThemeLock: lockValue,
  };
  const changed =
    (next.brandPrimary ?? '') !== (normalizeHex(stored.primary) ?? '') ||
    (next.brandAccent ?? '') !== (normalizeHex(stored.accent) ?? '') ||
    next.crewThemeLock !== stored.lock;
  const previewPrimary = normalizeHex(primary);
  const previewAccent = normalizeHex(accent);

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('settings:brand.heading')}
      </h2>
      <p className="mb-4 text-muted-foreground text-sm">
        {canEdit ? t('settings:brand.description') : t('settings:brand.ownersOnly')}
      </p>

      <fieldset disabled={!canEdit} className="grid min-w-0 gap-6">
        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <ColourField
            id="s-brand-primary"
            label={t('settings:brand.primary')}
            hint={t('settings:brand.primaryHint')}
            value={primary}
            onChange={setPrimary}
            check={primaryCheck}
            lowMessage={t('settings:brand.primaryLow')}
          />
          <ColourField
            id="s-brand-accent"
            label={t('settings:brand.accent')}
            hint={t('settings:brand.accentHint')}
            value={accent}
            onChange={setAccent}
            check={accentCheck}
            lowMessage={t('settings:brand.accentLow')}
          />
        </div>

        {previewPrimary || previewAccent ? (
          <div className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-white p-4">
            <span className="sr-only">{t('settings:brand.preview')}</span>
            {previewPrimary ? (
              <span
                aria-hidden="true"
                className="inline-flex h-11 items-center rounded-md px-5 font-semibold text-sm text-white"
                style={{ backgroundColor: previewPrimary }}
              >
                {t('settings:brand.previewButton')}
              </span>
            ) : null}
            {previewAccent ? (
              <span aria-hidden="true" className="inline-flex items-center gap-2 text-sm">
                <span className="size-4 rounded-full" style={{ backgroundColor: previewAccent }} />
                <span className="font-semibold" style={{ color: previewAccent }}>
                  {t('settings:brand.previewAccent')}
                </span>
              </span>
            ) : null}
          </div>
        ) : null}

        <label className="flex min-h-11 items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 size-5 shrink-0 accent-primary disabled:cursor-not-allowed"
            checked={lockValue}
            disabled={!bothSet}
            onChange={(e) => setLock(e.target.checked)}
          />
          <span>
            <span className="font-medium text-foreground text-sm">{t('settings:brand.lock')}</span>
            <span className="mt-1 block text-muted-foreground text-xs">
              {bothSet ? t('settings:brand.lockHint') : t('settings:brand.lockNeedsColours')}
            </span>
          </span>
        </label>
      </fieldset>

      {canEdit ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {save.isError ? (
            <p role="alert" className="mr-auto text-destructive text-sm">
              {t('settings:brand.saveError')}
            </p>
          ) : null}
          {save.isSuccess && !changed ? (
            <p role="status" className="mr-auto text-muted-foreground text-sm">
              {t('settings:brand.saved')}
            </p>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            disabled={save.isPending || !changed || !savable}
            onClick={() => save.mutate(next)}
          >
            {t('settings:brand.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
